import React, { useState, useEffect } from 'react';

const InstallPrompt = () => {
    const [installPrompt, setInstallPrompt] = useState(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);
    const [showIOSInstruction, setShowIOSInstruction] = useState(false);
    const [isChromeIOS, setIsChromeIOS] = useState(false);

    useEffect(() => {
        // 1. Detect Standalone Mode (Already Installed)
        const isStandaloneMode =
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

        setIsStandalone(isStandaloneMode);
        if (isStandaloneMode) return;

        // 2. Detect Platform
        const userAgent = window.navigator.userAgent;
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isCriOS = /CriOS/.test(userAgent); // Chrome on iOS

        setIsIOS(isIOSDevice);
        setIsChromeIOS(isCriOS);

        // 3. Capture Android/Desktop "beforeinstallprompt"
        if (window.pwaInstallPrompt) {
            setInstallPrompt(window.pwaInstallPrompt);
        }

        const handleInstallPrompt = (e) => {
            e.preventDefault();
            window.pwaInstallPrompt = e;
            setInstallPrompt(e);
            console.log('Capture Install Prompt');
        };

        window.addEventListener('beforeinstallprompt', handleInstallPrompt);

        const handleAppInstalled = () => {
            setInstallPrompt(null);
            setIsStandalone(true);
        };
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (installPrompt) {
            installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            if (outcome === 'accepted') {
                setInstallPrompt(null);
            }
        } else if (isIOS) {
            // Toggle instructions for iOS
            setShowIOSInstruction(!showIOSInstruction);
        }
    };

    // Do not render if already installed
    if (isStandalone) return null;

    // iOS Chrome Warning Modal
    if (isChromeIOS && showIOSInstruction) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm text-center shadow-2xl">
                    <h3 className="text-xl font-bold text-white mb-2">Open in Safari</h3>
                    <p className="text-gray-300 mb-4">
                        iOS does not allow installing apps from Chrome. Please open this page in <strong>Safari</strong> to install Optimus OS.
                    </p>
                    <button
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                        onClick={() => setShowIOSInstruction(false)}
                    >
                        Got it
                    </button>
                </div>
            </div>
        );
    }

    // iOS Safari "Add to Home Screen" Instructions
    if (isIOS && !isChromeIOS && showIOSInstruction) {
        return (
            <div className="fixed inset-x-0 bottom-0 z-50 bg-gray-900 border-t border-gray-700 p-6 rounded-t-2xl shadow-2xl animate-slide-up">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-bold text-white">Install Optimus OS</h3>
                    <button onClick={() => setShowIOSInstruction(false)} className="text-gray-400 hover:text-white">✕</button>
                </div>
                <div className="space-y-4 text-gray-300 text-sm">
                    <p className="flex items-center gap-3">
                        <span className="text-2xl">1️⃣</span>
                        <span>Tap the <strong>Share</strong> button (square with arrow) in the Safari menu bar.</span>
                    </p>
                    <p className="flex items-center gap-3">
                        <span className="text-2xl">2️⃣</span>
                        <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
                    </p>
                </div>
            </div>
        );
    }

    // Main Install Trigger Button
    // Only show if we have a prompt (Android/Desktop) OR we are on iOS (to show instructions)
    if (!installPrompt && !isIOS) return null;

    return (
        <>
            <button
                className="roi-dashboard-btn"
                onClick={handleInstallClick}
                title="Install Optimus OS to your device"
                style={{
                    background: 'linear-gradient(135deg, #007AFF 0%, #0055D4 100%)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
            >
                📱 {isIOS ? "Install App" : "Install App"}
            </button>
            {/* Render Chrome Warning Logic inline if needed, handled above via return override or state */}
        </>
    );
};

export default InstallPrompt;
